/*
================================================================================
                    WPQ TUBESHEET PAGE 2 - BUSINESS LOGIC
================================================================================

PURPOSE: Welding procedure parameters for tubesheet applications

KEY FEATURES:
• GTAW process parameters: Type, preheat/interpass temps, tungsten specs
• Value_A/Value_B arrays: Layer number, filler size, amperes, voltage, travel speed
• Gas specifications: Shielding, backing/trailing, auxiliary gas systems
• OTV parameters: Expansion, cleaning method, orifice cup, oscillation
• Post-weld heat treatment: Temperature, time, heating/cooling rates
• Certification details: Welder info, test conductors, lab references

DATA STRUCTURE:
• Dual-value arrays: [{Value_A: ["val1"]}, {Value_B: ["val2"]}]
• Process parameters: GTAW-specific welding variables
• Gas flow rates and compositions for tube integrity

BUSINESS RULES:
• GTAW default process for tubesheet welding
• Both Value_A and Value_B required for array fields
• Post-weld heat treatment critical for tube joints
• OTV parameters specific to orbital tube welding

================================================================================
*/

import { Row, Col, Table, Form } from "react-bootstrap";
import { useImperativeHandle, forwardRef, useState, useEffect } from "react";
import { Action, Status } from "../utility/common/constants/CommonConstants"
import { SingleInput, SingleInputTextAreaNotes } from "../utility/forms/inputs/InputsTransaction";

import { WPQ_PQR_Process_Grid_Input2_tube, Process_Grid_Input, Process_Grid_Input_2, GTAW_Process_Textbox } from "../utility/common/TransactionComponents/Common_WPS_WPQ_PQR_Process_Input_Fields";
import { isAllowCustomSpecialCharacters, isAlphanumericWithPlusAmpersandEqualsPercentMinus, isAlphanumericWithPlusAmpersandEqualsPlusMinus } from "../../functions/validations";
import { WPQHeader } from "./WPQObjects/WPQHeader";
import { AddNewDetailTubesheet } from "../../Services/wpq_details-service";
import { WPQ_tubesheet_page2, WPQ_Tube_Page2 } from "./WPQObjects/WPQ_entity";
import { AddNewGrooveWPQ } from "../../Services/wpq_groove-services";
import { emptyErrorWPQDetailTubesheet, handleCount } from "./WPQObjects/WPQCommonUtils";
import { validateForm, handleChangeNotesFlexible } from "../utility/common/TransactionComponents/TransactionValidationUtils";
import { handleWPQGeneralSubmit, handleWPQGeneralSubmitTemp } from "../utility/common/TransactionComponents/TransactionSubmitUtils";
import { DotLoader } from "react-spinners";
import PropTypes from "prop-types";
import { showWarning } from "../utility/ui/notifications/Toaster";
import { getWeldingProcessByProcess } from "../../Services/weldingprocess-services";

const WPQ_Tubesheet_Page2 = forwardRef(
  ({ headerObj, detailObj, setdetailObj, isDisabled, setAction }, ref) => {
    WPQ_Tubesheet_Page2.propTypes = {
      headerObj: PropTypes.array,
      setdetailObj: PropTypes.func,
      isDisabled: PropTypes.bool,
      detailObj: PropTypes.array,
      setAction: PropTypes.func
    };

    const [IsEdited, setIsEdited] = useState(false);
    const [loading, setLoading] = useState(false);
    const [activeWeldingProcess, setActiveWeldingProcess] = useState([]);

    const [error, setError] = useState({
      WPSH_WPS_No: {},
      WPSH_WPS_Revi_No: {},
      WPS_QualifiedTo: {},
      WPSH_WPS_Date: {},
      WPSH_SupportingPQR: {},
      WPQD_WP_WeldingProcess: {},
      WPQD_WP_Type: {},
      WPQD_WP_MinimumPreheatTemp: {},
      WPQD_WP_MaximumInterpassTemp: {},
      WPQD_WP_PostHeating: {},
      WPQD_WP_TungstenType: {},
      WPQD_WP_WeldPosition: {},
      WPQD_WP_WeldProgression: {},
      WPQD_WP_NoofPasses: {},
      WPQD_WP_LayerNumber: {},
      WPQD_WP_FillerMetalSIze: {},
      WPQD_WP_ItemCode: {},
      WPQD_WP_BatchNumber: {},
      WPQD_WP_Automatic_Arc_Voltage: {},
      WPQD_WP_Pulsing_current: {},
      WPQD_WP_Pulsing_current_others: {},
      WPQD_WP_Volts: {},
      WPQD_WP_TravelSpeed: {},
      WPQD_WP_HeatInput: {},
      WPQD_WP_CurrentPolarity: {},
      WPQD_WP_ArcVoltageControl: {},
      WPQD_WP_ShieldingGasType: {},
      WPQD_WP_ShieldingFlowRate: {},
      WPQD_WP_BackingGasType: {},
      WPQD_WP_BackingFlowRate: {},
      WPQD_WP_Auxiliarygas: {},
      WPQ_OTV_Expansion: {},
      WPQ_OTV_MethodofCleaning: {},
      WPQ_OTV_OrifceCupSize: {},
      WPQ_OTV_Oscillation: {},

      WPSH_PWHeatTre_Temperature: {},
      WPQ_Post_WHT_Time: {},
      WPSH_PWHeatTre_Type: {},
      WPQ_Post_WHT_Heating_Rate: {},
      WPQ_Post_WHT_Cooling_Rate: {},
      WPSH_Notes: {},
      WPSH_PreparedBy_Name: {},
      WPSH_PreparedBy_Date: {},
      WPSH_ApprovedBy_Name: {},
      WPSH_ApprovedBy_Date: {},
      WPSH_PreparedBy_Remark: {},
      WPSH_ApprovedBy_Remark: {},

      WPQD_WP_LayerNo: [{ Value_A: [""] }, { Value_B: [""] }],
      WPQD_WP_Filler_Metal_Size: [{ Value_A: [""] }, { Value_B: [""] }],

      WPQD_WP_Amperes: [{ Value_A: [""] }, { Value_B: [""] }],
      WPQD_WP_DC_Pulsing_current: [{ Value_A: [""] }, { Value_B: [""] }],
      WPQD_WP_DC_Pulsing_current_Others: [{ Value_A: [""] }, { Value_B: [""] }],
      WPQD_WP_Volatage_Volts: [{ Value_A: [""] }, { Value_B: [""] }],
      WPQD_WP_Travel_Speed: [{ Value_A: [""] }, { Value_B: [""] }],
      WPQD_WP_Heat_Input: [{ Value_A: [""] }, { Value_B: [""] }],
      WPQD_WP_PolarityOrCurrent: [{ Value_A: [""] }, { Value_B: [""] }],
      WPQ_Post_WHT_Temp: {},
      WPQ_Post_WHT_Type: {},
      WPQ_Page2_Notes: {},
      WPQ_Certification_Welder_Name: {},
      WPQ_Certification_ID: {},
      WPQ_Certification_ConductedBy: {},
      WPQ_Certification_Mechanical_TestsBy: {},
      WPQ_Certification_Lab_RefNo: {},
    });

    const validate = async () => {
      let temperror = emptyErrorWPQDetailTubesheet(error, setError, false);

      // 1. Use common validation for simple header fields
      const validationResult = validateForm({
        headerObj: detailObj,
        detailObj,
        requiredFields: WPQ_Tube_Page2,
        detailConfig: [], // Empty - no array validation here
        currentErrors: error,
      });

      let isError = validationResult.isError;
      let finalErrors = validationResult.errors;

      // VALIDATION: Value_A/Value_B dual arrays for tubesheet parameters
      let arrayFields = WPQ_tubesheet_page2;
      arrayFields.forEach((cols) => {
        let valueA = detailObj[cols.Column]?.[0]?.Value_A?.[0];
        let valueB = detailObj[cols.Column]?.[1]?.Value_B?.[0];

        if (!valueA || valueA === "" || !valueB || valueB === "") {
          isError = true;
          finalErrors[cols.Column] = [
            { Value_A: [{ status: true, message: "This field is required" }] },
            { Value_B: [{ status: true, message: "This field is required" }] }
          ];
        }
      });

      // 3. Apply all errors at once
      setError(finalErrors);

      return { isError, temperror };
    };

     useEffect(() => {
      fetchProcess();
    }, []);


    const fetchProcess = async () => {
      const result = await getWeldingProcessByProcess({ WPSD_Process: "GTAW" });
      let processMap =[];
      if (result?.length > 0) {
        processMap = new Map(result.map(p => [p.wld_code, p.wld_p_category]));
      }
      setActiveWeldingProcess(processMap);
    };


    const handle_2Input_ChangeA = (key, id, valueIndex, newValue) => {
      setIsEdited(true);
      const updatedProcessObj = { ...detailObj };

      updatedProcessObj[key][id].Value_A[valueIndex] = newValue;

      setdetailObj(updatedProcessObj);
    };

    const handle_2Input_ChangeB = (key, id, valueIndex, newValue) => {
      setIsEdited(true);
      const updatedProcessObj = { ...detailObj };

      updatedProcessObj[key][id].Value_B[valueIndex] = newValue;
      setdetailObj(updatedProcessObj);
    };

    const handleAdd = async () => {
      let result = false;
      headerObj.WPQD_WP_WeldingProcess = "GTAW";
      if (
        headerObj?.WPSH_Status === Status.StatusApproved ||
        headerObj?.WPSH_Status === Status.StatusRejected
      ) {
        headerObj.WPSH_Status = Status.StatusPending;
        if (headerObj?.WPSH_Status === Status.StatusApproved) handleCount();
      }

      headerObj.WPQD_WP_Amperes = detailObj.WPQD_WP_Amperes;
      headerObj.WPQD_WP_NoofPasses = detailObj.WPQD_WP_NoofPasses;
      headerObj.WPQD_WP_WeldPosition = detailObj.WPQD_WP_WeldPosition;
      headerObj.WPQD_WP_WeldProgression = detailObj.WPQD_WP_WeldProgression;
      headerObj.WPQD_WP_PolarityOrCurrent = detailObj.WPQD_WP_PolarityOrCurrent;
      headerObj.WPQD_WP_Automatic_Arc_Voltage =
        detailObj.WPQD_WP_Automatic_Arc_Voltage;
      headerObj.WPSD_FM_FNo = detailObj.WPSD_FM_FNo;
      headerObj.WPSD_FM_Ano = detailObj.WPSD_FM_Ano;
      headerObj.WPQD_WP_Type = detailObj.WPQD_WP_Type;
      const wldProcessCategory = activeWeldingProcess.get("GTAW");

      let wpqProcessCategory = wldProcessCategory +"#"+detailObj.WPQD_WP_Type;

      setdetailObj((prevState) => ({
        ...prevState,
        WPQ_Process_Category: wpqProcessCategory,
      }));
      detailObj.WPQ_Process_Category = wpqProcessCategory;
      let res = await AddNewGrooveWPQ(headerObj);
      let resdet = await AddNewDetailTubesheet(detailObj);

      if (res !== undefined && resdet !== undefined) {
        result = true;

        setAction(Action.ActionEdit);

      } else {
        showWarning(process.env.REACT_APP_SPECIAL_CHARACTER_MSG);
      }
      return result;
    };

    const validateTemp = async () => {
      let isError = false;
      let temperror = emptyErrorWPQDetailTubesheet(error, setError, isError);
      return { isError, temperror };
    };

    const handleSubmit = () => handleWPQGeneralSubmit(validate, handleAdd, setLoading, setIsEdited, Status.StatusPending);
    const handleSubmitTemp = () => handleWPQGeneralSubmitTemp(IsEdited, validateTemp, handleAdd, setLoading, setIsEdited, headerObj?.WPQH_Status || Status.StatusTempSaved);

    useImperativeHandle(ref, () => ({
      funcOnNext2: async () => {
        return await handleSubmit();
      },
      funcOnNextTemp2: async () => {
        return await handleSubmitTemp();
      },
    }));



    const handleChangedetail = (e) => {
      setIsEdited(true);
      const { name, value } = e.target;
      setdetailObj((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    };

    const [valueNotes, setValueNotes] = useState("");

    const handleChangeNotes = (e) => {
      handleChangeNotesFlexible(e, {
        setIsEdited,
        setStateObj: setdetailObj,
        setValueNotes,
        maxNewLines: 5,
        maxLineLength: 100
      });
    };

    // Calculate toggle editability (178-day rule)
    const rawDate = detailObj?.wld_createdon;
    let isToggleEditable = false;
    if (rawDate) {
      const [day, month, year] = rawDate.split("-");
      const createdDate = new Date(`${year}-${month}-${day}`);
      createdDate.setHours(0, 0, 0, 0);
      const expiryDate = new Date(createdDate);
      expiryDate.setDate(expiryDate.getDate() + 178);
      expiryDate.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      isToggleEditable = today <= expiryDate;
    }

    return (
      <fieldset disabled={isDisabled}>
        <Row className="font">
          {loading && (
            <div className="loader-overlay">
              <DotLoader size={80} className="pageloader" />
            </div>
          )}
          <WPQHeader headerObj={headerObj}></WPQHeader>
          <br />
          <Row style={{ padding: "0px 60px" }}>
            <span className="WPS-blue-text" style={{ position: "relative", top: "20px" }}>
              Welding Procedure
            </span>
            <Row className="mb-4" style={{ padding: "10px 25px" }}>
              <div className="statusbg">
                <span
                  className="statusborder float-start"
                  style={{ display: "flex", alignItems: "center", gap: "2px" }}
                >
                  <span style={{ marginRight: "-10px" }}>Status</span>
                  <Form.Switch
                    disabled={!isToggleEditable}
                    name="wld_process_status"
                    checked={detailObj?.wld_process_status === "Active"}
                    className={
                      detailObj?.wld_process_status === "Active"
                        ? "semiBoldToggleButton activeToggleLabel"
                        : "semiBoldToggleButton inactiveToggleLabel"
                    }
                    onChange={(e) => {
                      if (!isToggleEditable) return;

                      const newStatus = e.target.checked ? "Active" : "Inactive";
                      const updatedDetail = { ...detailObj, wld_process_status: newStatus };

                      setdetailObj(updatedDetail);

                      setIsEdited(true);
                    }}
                  />
                  <span
                    style={{
                      marginLeft: "4px",
                      color:
                        detailObj?.wld_process_status === "Inactive"
                          ? "var(--godrej-orange)"
                          : "var(--godrej-green-alt)",
                    }}
                  >
                    {detailObj?.wld_process_status}
                  </span>
                </span>
                <span className=" statusborder float-end">
                  Last Weld Date &nbsp;
                  <span style={{ color: "var(--godrej-green-alt)" }}>
                    {" "}
                    {detailObj?.wld_createdon}
                  </span>{" "}
                </span>
              </div>
            </Row>

            <Table
              bordered
              responsive="sm"
              className="text-center tube-col-widths"
              style={{ position: "relative", bottom: "10px" }}
            >
              <tbody>
                <GTAW_Process_Textbox
                  ColumnHeader="Welding Process"
                  status={error.WPQD_WP_WeldingProcess.status}
                  message={error.WPQD_WP_WeldingProcess.message}
                  name="WPQD_WP_WeldingProcess"
                  width="512px"
                ></GTAW_Process_Textbox>
                <Process_Grid_Input
                  detailObj={detailObj}
                  maxLength={10}
                  ColumnHeader="Type"
                  isDisabled={isDisabled}
                  status={error.WPQD_WP_Type.status}
                  message={error.WPQD_WP_Type.message}
                  type="text"
                  name="WPQD_WP_Type"
                  width="512px"
                  setError={setError}
                  handleChangedetailsarray={handleChangedetail}
                  //handleOnBlurdetailsarray={handleOnBlurdetail}
                ></Process_Grid_Input>
                <Process_Grid_Input
                  detailObj={detailObj}
                  maxLength={18}
                  setError={setError}
                  ColumnHeader="Minimunm Preheat Temp"
                  Columndimension="°C"
                  status={error.WPQD_WP_MinimumPreheatTemp.status}
                  message={error.WPQD_WP_MinimumPreheatTemp.message}
                  type="text"
                  name="WPQD_WP_MinimumPreheatTemp"
                  width="512px"
                  isDisabled={isDisabled}
                  handleChangedetailsarray={handleChangedetail}
                ></Process_Grid_Input>
                <Process_Grid_Input
                  detailObj={detailObj}
                  maxLength={18}
                  setError={setError}
                  ColumnHeader=" Maximum Interpass Temp"
                  Columndimension="°C"
                  status={error.WPQD_WP_MaximumInterpassTemp.status}
                  message={error.WPQD_WP_MaximumInterpassTemp.message}
                  type="text"
                  name="WPQD_WP_MaximumInterpassTemp"
                  width="512px"
                  isDisabled={isDisabled}
                  handleChangedetailsarray={handleChangedetail}
                ></Process_Grid_Input>
                <Process_Grid_Input
                  detailObj={detailObj}
                  maxLength={48}
                  setError={setError}
                  ColumnHeader="Post Heating/DHT"
                  status={error.WPQD_WP_PostHeating.status}
                  message={error.WPQD_WP_PostHeating.message}
                  type="text"
                  name="WPQD_WP_PostHeating"
                  width="512px"
                  isDisabled={isDisabled}
                  handleChangedetailsarray={handleChangedetail}
                ></Process_Grid_Input>
                <Process_Grid_Input
                  detailObj={detailObj}
                  maxLength={48}
                  setError={setError}
                  ColumnHeader="Tungsten Type and Size"
                  Columndimension="mm"
                  status={error.WPQD_WP_TungstenType.status}
                  message={error.WPQD_WP_TungstenType.message}
                  type="text"
                  name="WPQD_WP_TungstenType"
                  width="512px"
                  isDisabled={isDisabled}
                  handleChangedetailsarray={handleChangedetail}
                ></Process_Grid_Input>
                <Process_Grid_Input
                  detailObj={detailObj}
                  maxLength={48}
                  setError={setError}
                  ColumnHeader="Welding Position"
                  status={error.WPQD_WP_WeldPosition.status}
                  message={error.WPQD_WP_WeldPosition.message}
                  type="text"
                  name="WPQD_WP_WeldPosition"
                  width="512px"
                  isDisabled={isDisabled}
                  handleChangedetailsarray={handleChangedetail}
                ></Process_Grid_Input>
                <Process_Grid_Input
                  detailObj={detailObj}
                  maxLength={48}
                  setError={setError}
                  ColumnHeader="Progression"
                  status={error.WPQD_WP_WeldProgression.status}
                  message={error.WPQD_WP_WeldProgression.message}
                  type="text"
                  name="WPQD_WP_WeldProgression"
                  width="512px"
                  isDisabled={isDisabled}
                  handleChangedetailsarray={handleChangedetail}
                ></Process_Grid_Input>
                <Process_Grid_Input
                  detailObj={detailObj}
                  maxLength={4}
                  setError={setError}
                  ColumnHeader="No of Passes"
                  status={error.WPQD_WP_NoofPasses.status}
                  message={error.WPQD_WP_NoofPasses.message}
                  type="text"
                  name="WPQD_WP_NoofPasses"
                  width="512px"
                  isDisabled={isDisabled}
                  handleChangedetailsarray={handleChangedetail}
                ></Process_Grid_Input>
              </tbody>
            </Table>

            <Table
              bordered
              responsive="sm"
              // className="text-center same-col-widths align-middle"
              className="text-center tube-col-widths"
              style={{ position: "relative", bottom: "30px" }}
            >
              <tbody>
                <WPQ_PQR_Process_Grid_Input2_tube
                  detailObj={detailObj}
                  maxLength={20}
                  readOnly={isDisabled}
                  ColumnHeader="Layer Number"
                  status={error.WPQD_WP_LayerNo[0].Value_A[0].status}
                  message={error.WPQD_WP_LayerNo[0].Value_A[0].message}
                  status1={error.WPQD_WP_LayerNo[1].Value_B[0].status}
                  message1={error.WPQD_WP_LayerNo[1].Value_B[0].message}
                  type="text"
                  name="WPQD_WP_LayerNo"
                  handle_2Input_ChangeA={handle_2Input_ChangeA}
                  handle_2Input_ChangeB={handle_2Input_ChangeB}
                  setError={setError}
                  error={error}
                ></WPQ_PQR_Process_Grid_Input2_tube>

                <WPQ_PQR_Process_Grid_Input2_tube
                  detailObj={detailObj}
                  maxLength={20}
                  readOnly={isDisabled}
                  ColumnHeader="Filler Metal Size"
                  Columndimension="mm"
                  status={error.WPQD_WP_Filler_Metal_Size[0].Value_A[0].status}
                  message={
                    error.WPQD_WP_Filler_Metal_Size[0].Value_A[0].message
                  }
                  status1={error.WPQD_WP_Filler_Metal_Size[1].Value_B[0].status}
                  message1={
                    error.WPQD_WP_Filler_Metal_Size[1].Value_B[0].message
                  }
                  type="text"
                  name="WPQD_WP_Filler_Metal_Size"
                  handle_2Input_ChangeA={handle_2Input_ChangeA}
                  handle_2Input_ChangeB={handle_2Input_ChangeB}
                  setError={setError}
                  error={error}
                ></WPQ_PQR_Process_Grid_Input2_tube>

                <WPQ_PQR_Process_Grid_Input2_tube
                  detailObj={detailObj}
                  maxLength={20}
                  ColumnHeader="Amperes Amps"
                  readOnly={isDisabled}
                  status={error.WPQD_WP_Amperes[0].Value_A[0].status}
                  message={error.WPQD_WP_Amperes[0].Value_A[0].message}
                  status1={error.WPQD_WP_Amperes[1].Value_B[0].status}
                  message1={error.WPQD_WP_Amperes[1].Value_B[0].message}
                  type="text"
                  name="WPQD_WP_Amperes"
                  handle_2Input_ChangeA={handle_2Input_ChangeA}
                  handle_2Input_ChangeB={handle_2Input_ChangeB}
                  setError={setError}
                  error={error}
                ></WPQ_PQR_Process_Grid_Input2_tube>

                <WPQ_PQR_Process_Grid_Input2_tube
                  detailObj={detailObj}
                  maxLength={20}
                  ColumnHeader="DC Pulsing Current"
                  Columndimension="Amps"
                  readOnly={isDisabled}
                  status={error.WPQD_WP_DC_Pulsing_current[0].Value_A[0].status}
                  message={
                    error.WPQD_WP_DC_Pulsing_current[0].Value_A[0].message
                  }
                  status1={
                    error.WPQD_WP_DC_Pulsing_current[1].Value_B[0].status
                  }
                  message1={
                    error.WPQD_WP_DC_Pulsing_current[1].Value_B[0].message
                  }
                  type="text"
                  name="WPQD_WP_DC_Pulsing_current"
                  handle_2Input_ChangeA={handle_2Input_ChangeA}
                  handle_2Input_ChangeB={handle_2Input_ChangeB}
                  setError={setError}
                  error={error}
                ></WPQ_PQR_Process_Grid_Input2_tube>

                <WPQ_PQR_Process_Grid_Input2_tube
                  detailObj={detailObj}
                  maxLength={20}
                  ColumnHeader="DC Pulsing Current-Others"
                  Columndimension=""
                  readOnly={isDisabled}
                  status={
                    error.WPQD_WP_DC_Pulsing_current_Others[0].Value_A[0].status
                  }
                  message={
                    error.WPQD_WP_DC_Pulsing_current_Others[0].Value_A[0]
                      .message
                  }
                  status1={
                    error.WPQD_WP_DC_Pulsing_current_Others[1].Value_B[0].status
                  }
                  message1={
                    error.WPQD_WP_DC_Pulsing_current_Others[1].Value_B[0]
                      .message
                  }
                  type="text"
                  name="WPQD_WP_DC_Pulsing_current_Others"
                  handle_2Input_ChangeA={handle_2Input_ChangeA}
                  handle_2Input_ChangeB={handle_2Input_ChangeB}
                  setError={setError}
                  error={error}
                ></WPQ_PQR_Process_Grid_Input2_tube>
                <WPQ_PQR_Process_Grid_Input2_tube
                  detailObj={detailObj}
                  maxLength={20}
                  ColumnHeader="Voltage Volts"
                  Columndimension="Kj/mm"
                  readOnly={isDisabled}
                  status={error.WPQD_WP_Volatage_Volts[0].Value_A[0].status}
                  message={error.WPQD_WP_Volatage_Volts[0].Value_A[0].message}
                  status1={error.WPQD_WP_Volatage_Volts[1].Value_B[0].status}
                  message1={error.WPQD_WP_Volatage_Volts[1].Value_B[0].message}
                  type="text"
                  name="WPQD_WP_Volatage_Volts"
                  setError={setError}
                  error={error}
                  handle_2Input_ChangeA={handle_2Input_ChangeA}
                  handle_2Input_ChangeB={handle_2Input_ChangeB}
                ></WPQ_PQR_Process_Grid_Input2_tube>

                <WPQ_PQR_Process_Grid_Input2_tube
                  detailObj={detailObj}
                  maxLength={20}
                  ColumnHeader="Travel Speed"
                  Columndimension="mm/min"
                  readOnly={isDisabled}
                  status={error.WPQD_WP_Travel_Speed[0].Value_A[0].status}
                  message={error.WPQD_WP_Travel_Speed[0].Value_A[0].message}
                  status1={error.WPQD_WP_Travel_Speed[1].Value_B[0].status}
                  message1={error.WPQD_WP_Travel_Speed[1].Value_B[0].message}
                  type="text"
                  name="WPQD_WP_Travel_Speed"
                  setError={setError}
                  error={error}
                  handle_2Input_ChangeA={handle_2Input_ChangeA}
                  handle_2Input_ChangeB={handle_2Input_ChangeB}
                ></WPQ_PQR_Process_Grid_Input2_tube>

                <WPQ_PQR_Process_Grid_Input2_tube
                  detailObj={detailObj}
                  maxLength={20}
                  ColumnHeader="Heat Input"
                  Columndimension="Kj/mm"
                  readOnly={isDisabled}
                  status={error.WPQD_WP_Heat_Input[0].Value_A[0].status}
                  message={error.WPQD_WP_Heat_Input[0].Value_A[0].message}
                  status1={error.WPQD_WP_Heat_Input[1].Value_B[0].status}
                  message1={error.WPQD_WP_Heat_Input[1].Value_B[0].message}
                  type="text"
                  name="WPQD_WP_Heat_Input"
                  setError={setError}
                  error={error}
                  handle_2Input_ChangeA={handle_2Input_ChangeA}
                  handle_2Input_ChangeB={handle_2Input_ChangeB}
                ></WPQ_PQR_Process_Grid_Input2_tube>
                <WPQ_PQR_Process_Grid_Input2_tube
                  detailObj={detailObj}
                  maxLength={20}
                  ColumnHeader="Polarity or Current type"
                  status={error.WPQD_WP_PolarityOrCurrent[0].Value_A[0].status}
                  message={
                    error.WPQD_WP_PolarityOrCurrent[0].Value_A[0].message
                  }
                  status1={error.WPQD_WP_PolarityOrCurrent[1].Value_B[0].status}
                  message1={
                    error.WPQD_WP_PolarityOrCurrent[1].Value_B[0].message
                  }
                  type="text"
                  name="WPQD_WP_PolarityOrCurrent"
                  setError={setError}
                  error={error}
                  readOnly={isDisabled}
                  handle_2Input_ChangeA={handle_2Input_ChangeA}
                  handle_2Input_ChangeB={handle_2Input_ChangeB}
                ></WPQ_PQR_Process_Grid_Input2_tube>
              </tbody>
            </Table>

            <Table
              bordered
              responsive="sm"
              // className="text-center same-col-widths align-middle"
              className="text-center tube-col-widths"
              style={{ position: "relative", bottom: "50px" }}
            >
              <tbody>
                <Process_Grid_Input
                  detailObj={detailObj}
                  maxLength={20}
                  setError={setError}
                  ColumnHeader="Automatic Arc Voltage Control"
                  status={error.WPQD_WP_Automatic_Arc_Voltage.status}
                  message={error.WPQD_WP_Automatic_Arc_Voltage.message}
                  type="text"
                  name="WPQD_WP_Automatic_Arc_Voltage"
                  width="512px"
                  disabled={isDisabled}
                  handleChangedetailsarray={handleChangedetail}
                ></Process_Grid_Input>

                <Process_Grid_Input_2
                  detailObj={detailObj}
                  maxLength={48}
                  setError={setError}
                  mainheading="Shielding"
                  ColumnHeader="Gas Type - % comp"
                  ColumnHeader1="Flow Type"
                  Columndimension="I/min"
                  width="512px"
                  status={
                    error.WPQD_WP_ShieldingGasType.status ||
                    error.WPQD_WP_ShieldingFlowRate.status
                  }
                  message={
                    error.WPQD_WP_ShieldingGasType.message ||
                    error.WPQD_WP_ShieldingFlowRate.message
                  }
                  type="text"
                  readOnly={isDisabled}
                  name="WPQD_WP_ShieldingGasType"
                  name1="WPQD_WP_ShieldingFlowRate"
                  handleChangedetailsarray={handleChangedetail}
                  regexValidator1={isAlphanumericWithPlusAmpersandEqualsPercentMinus}
                  regexValidator2={isAlphanumericWithPlusAmpersandEqualsPlusMinus}

                ></Process_Grid_Input_2>

                <Process_Grid_Input_2
                  detailObj={detailObj}
                  maxLength={48}
                  setError={setError}
                  mainheading="Backing / Trailing"
                  ColumnHeader="Gas Type - % comp"
                  ColumnHeader1="Flow Type"
                  Columndimension="I/min"
                  status={
                    error.WPQD_WP_BackingGasType.status ||
                    error.WPQD_WP_BackingFlowRate.status
                  }
                  message={
                    error.WPQD_WP_BackingGasType.message ||
                    error.WPQD_WP_BackingFlowRate.message
                  }
                  type="text"
                  name="WPQD_WP_BackingGasType"
                  name1="WPQD_WP_BackingFlowRate"
                  width="512px"
                  handleChangedetailsarray={handleChangedetail}
                  readOnly={isDisabled}
                  regexValidator1={isAlphanumericWithPlusAmpersandEqualsPercentMinus}
                  regexValidator2={isAlphanumericWithPlusAmpersandEqualsPlusMinus}
                ></Process_Grid_Input_2>
                <Process_Grid_Input
                  detailObj={detailObj}
                  maxLength={48}
                  setError={setError}
                  ColumnHeader="Auxiliarygas shield system"
                  status={error.WPQD_WP_Auxiliarygas.status}
                  message={error.WPQD_WP_Auxiliarygas.message}
                  type="text"
                  name="WPQD_WP_Auxiliarygas"
                  width="512px"
                  disabled={isDisabled}
                  handleChangedetailsarray={handleChangedetail}
                ></Process_Grid_Input>
              </tbody>
            </Table>

            <Row style={{ position: "relative", bottom: "50px", left: "25px" }}>
              <span
                className="WPS-blue-text"
                style={{ position: "relative", right: "11px", top: "10px" }}
              >
                Other Variables
              </span>
              <Row className="mb-1">
                <Col md={3}>
                  <SingleInput
                    disabled={isDisabled}
                    name="WPQ_OTV_Expansion"
                    type="text"
                    maxLength={48}
                    status={error.WPQ_OTV_Expansion.status}
                    value={detailObj?.WPQ_OTV_Expansion}
                    onChange={handleChangedetail}
                    label="Expansion"
                    message={error.WPQ_OTV_Expansion.message}
                    setError={setError}
                    error={error}

                  ></SingleInput>
                </Col>
                <Col md={3}>
                  <SingleInput
                    disabled={isDisabled}
                    name="WPQ_OTV_MethodofCleaning"
                    type="text"
                    maxLength={48}
                    status={error.WPQ_OTV_MethodofCleaning.status}
                    value={detailObj?.WPQ_OTV_MethodofCleaning}
                    onChange={handleChangedetail}
                    label=" Method of Cleaning"
                    message={error.WPQ_OTV_MethodofCleaning.message}
                    setError={setError}
                    error={error}
                  ></SingleInput>
                </Col>
                <Col md={3}>
                  <SingleInput
                    disabled={isDisabled}
                    name="WPQ_OTV_OrifceCupSize"
                    type="text"
                    maxLength={48}
                    status={error.WPQ_OTV_OrifceCupSize.status}
                    value={detailObj?.WPQ_OTV_OrifceCupSize}
                    onChange={handleChangedetail}
                    label="Orifce Cup Size"
                    message={error.WPQ_OTV_OrifceCupSize.message}
                    setError={setError}
                    error={error}
                  ></SingleInput>
                </Col>
                <Col md={3}>
                  <SingleInput
                    disabled={isDisabled}
                    name="WPQ_OTV_Oscillation"
                    type="text"
                    maxLength={20}
                    status={error.WPQ_OTV_Oscillation.status}
                    value={detailObj?.WPQ_OTV_Oscillation}
                    onChange={handleChangedetail}
                    label="Oscillation"
                    message={error.WPQ_OTV_Oscillation.message}
                    setError={setError}
                    error={error}
                  ></SingleInput>
                </Col>
              </Row>
            </Row>

            <Row style={{ position: "relative", left: "25px", bottom: "50px" }}>
              <span
                className="WPS-blue-text"
                style={{ position: "relative", right: "11px", top: "10px" }}
              >
                Post Weld Heat Treatment Tubesheet
              </span>
              <Row className="mb-1">
                <Col className="mb-1" md={3}>
                  <SingleInput
                    disabled={isDisabled}
                    name="WPQ_Post_WHT_Temp"
                    type="text"
                    maxLength={25}
                    status={error.WPQ_Post_WHT_Temp.status}
                    value={detailObj?.WPQ_Post_WHT_Temp}
                    onChange={handleChangedetail}
                    label="Post Weld H/T Temp(°C)"
                    message={error.WPQ_Post_WHT_Temp.message}
                    setError={setError}
                    error={error}
                    allowlistfunction={isAllowCustomSpecialCharacters}
                    allowcharacters="&±+-="
                    customMessage="Special Characters #  % + $ ' < >  ; not allowed"
                  ></SingleInput>
                </Col>

                <Col className="mb-1" md={3}>
                  <div className="T-input w-100">
                    <input
                      type="text"
                      id="WPQ_Post_WHT_Time"
                      name="WPQ_Post_WHT_Time"
                      maxLength={20}
                      disabled={isDisabled}
                      placeholder=" "
                      className={
                        error.WPQ_Post_WHT_Time.status
                          ? " T-input placeHolderInput inputText text-field-with-error"
                          : "T-input placeHolderInput inputText"
                      }
                      value={detailObj?.WPQ_Post_WHT_Time}
                      onChange={handleChangedetail}
                      onInput={(e) => {
                        let error = isAlphanumericWithPlusAmpersandEqualsPlusMinus(
                          e.target.value
                        );

                        setError((prevState) => ({
                          ...prevState,
                          WPQ_Post_WHT_Time: error,
                        }));
                      }}
                    />
                    <Form.Label htmlFor="WPQ_Post_WHT_Time">Time (Minutes)</Form.Label>
                  </div>
                  {detailObj?.WPQ_Post_WHT_Temp !== "" ? (
                    <div className="required-text1">
                      {error.WPQ_Post_WHT_Time.status && (
                        <Form.Text className="text-danger">
                          {error.WPQ_Post_WHT_Time.message}
                        </Form.Text>
                      )}
                    </div>
                  ) : (
                    ""
                  )}
                </Col>
                <Col className="mb-1" md={3}>
                  <SingleInput
                    disabled={isDisabled}
                    name="WPQ_Post_WHT_Type"
                    type="text"
                    maxLength={20}
                    status={error.WPQ_Post_WHT_Type.status}
                    value={detailObj?.WPQ_Post_WHT_Type}
                    onChange={handleChangedetail}
                    label="Type"
                    message={error.WPQ_Post_WHT_Type.message}
                    setError={setError}
                    error={error}
                  ></SingleInput>
                </Col>
                <Col className="mb-1" md={3}>
                  <div className="T-input w-100">
                    <input
                      type="text"
                      id="WPQ_Post_WHT_Heating_Rate"
                      name="WPQ_Post_WHT_Heating_Rate"
                      maxLength={25}
                      disabled={isDisabled}
                      placeholder=" "
                      className={
                        error.WPQ_Post_WHT_Heating_Rate.status
                          ? " T-input placeHolderInput inputText text-field-with-error"
                          : "T-input placeHolderInput inputText"
                      }
                      value={detailObj?.WPQ_Post_WHT_Heating_Rate}
                      onChange={handleChangedetail}
                      onInput={(e) => {
                        let error = isAlphanumericWithPlusAmpersandEqualsPlusMinus(
                          e.target.value
                        );
                        setError((prevState) => ({
                          ...prevState,
                          WPQ_Post_WHT_Heating_Rate: error,
                        }));
                      }}
                    />
                    <Form.Label htmlFor="WPQ_Post_WHT_Heating_Rate">
                      Heating Rate (C/Hr)
                    </Form.Label>
                  </div>
                  {detailObj?.WPQ_Post_WHT_Temp !== "" ? (
                    <div className="required-text1">
                      {error.WPQ_Post_WHT_Heating_Rate.status && (
                        <Form.Text className="text-danger">
                          {error.WPQ_Post_WHT_Heating_Rate.message}
                        </Form.Text>
                      )}
                    </div>
                  ) : (
                    ""
                  )}
                </Col>
              </Row>
              <Row className="mb-1">
                <Col className="mb-1" md={3}>
                  <SingleInput
                    disabled={isDisabled}
                    name="WPQ_Post_WHT_Cooling_Rate"
                    type="text"
                    maxLength={20}
                    status={error.WPQ_Post_WHT_Cooling_Rate.status}
                    value={detailObj?.WPQ_Post_WHT_Cooling_Rate}
                    onChange={handleChangedetail}
                    label=" Cooling Rate (C/Hr)"
                    message={error.WPQ_Post_WHT_Cooling_Rate.message}
                    setError={setError}
                    error={error}
                    allowlistfunction={isAllowCustomSpecialCharacters}
                    allowcharacters="&±+-="
                    customMessage="Special Characters # % + $ ' < >  ; not allowed"
                  ></SingleInput>
                </Col>
              </Row>
            </Row>
          </Row>
          <Row
            className="WPQ-Page-row-padding"
            style={{
              padding: "20px 60px",
              position: "relative",
              bottom: "70px",
            }}
          >
            <span className="WPS-blue-text" style={{ position: "relative", top: "10px" }}>
              Notes
            </span>
            <Col md={12}>
              <SingleInputTextAreaNotes
                name="WPQ_Page2_Notes"
                type="text"
                style={{ height: "250px", resize: "none" }}
                maxLength={1500}
                className="textarea-notes"
                status={error.WPQ_Page2_Notes.status}
                value={
                  detailObj?.WPQ_Page2_Notes
                    ? detailObj?.WPQ_Page2_Notes
                    : valueNotes
                }
                handleChange={handleChangeNotes}
                label="Notes"
                message={error.WPQ_Page2_Notes.message}
                disabled={isDisabled}
              ></SingleInputTextAreaNotes>
            </Col>
            <span
              className="WPS-blue-text"
              style={{ position: "relative", top: "10px" }}
            >
              Certification
            </span>
            <Col md={6}>
              <SingleInput
                disabled={isDisabled}
                name="WPQ_Certification_Welder_Name"
                type="text"
                maxLength={20}
                status={error.WPQ_Certification_Welder_Name.status}
                value={detailObj?.WPQ_Certification_Welder_Name}
                onChange={handleChangedetail}
                label="Welder's Name"
                message={error.WPQ_Certification_Welder_Name.message}
                setError={setError}
                error={error}
                allowlistfunction={isAllowCustomSpecialCharacters}
                allowcharacters="&-"
                customMessage="Special Characters # % + $ ' < > = ; not allowed"
              ></SingleInput>
            </Col>
            <Col md={3}>
              <SingleInput
                disabled={isDisabled}
                name="WPQ_Certification_ID"
                type="text"
                maxLength={20}
                status={error.WPQ_Certification_ID.status}
                value={detailObj?.WPQ_Certification_ID}
                onChange={handleChangedetail}
                label="ID"
                message={error.WPQ_Certification_ID.message}
                setError={setError}
                error={error}
                allowlistfunction={isAllowCustomSpecialCharacters}
                allowcharacters="&-"
                customMessage="Special Characters # % + $ ' < > = ; not allowed"
              ></SingleInput>
            </Col>
            <Col md={3}>
              <SingleInput
                disabled={isDisabled}
                name="WPQ_Certification_ConductedBy"
                type="text"
                maxLength={25}
                status={error.WPQ_Certification_ConductedBy.status}
                value={detailObj?.WPQ_Certification_ConductedBy}
                onChange={handleChangedetail}
                label="Tests Conducted by"
                message={error.WPQ_Certification_ConductedBy.message}
                setError={setError}
                error={error}
                allowlistfunction={isAllowCustomSpecialCharacters}
                allowcharacters="&-"
                customMessage="Special Characters # % + $ ' < > = ; not allowed"
              ></SingleInput>
            </Col>
            <Col md={3}>
              <SingleInput
                disabled={isDisabled}
                name="WPQ_Certification_Mechanical_TestsBy"
                type="text"
                maxLength={25}
                status={error.WPQ_Certification_Mechanical_TestsBy.status}
                value={detailObj?.WPQ_Certification_Mechanical_TestsBy}
                onChange={handleChangedetail}
                label="Mechanical Tests by"
                message={error.WPQ_Certification_Mechanical_TestsBy.message}
                setError={setError}
                error={error}
                allowlistfunction={isAllowCustomSpecialCharacters}
                allowcharacters="&-"
                customMessage="Special Characters # % + $ ' < > = ; not allowed"
              ></SingleInput>
            </Col>
            <Col md={6}>
              <SingleInput
                disabled={isDisabled}
                name="WPQ_Certification_Lab_RefNo"
                type="text"
                maxLength={60}
                status={error.WPQ_Certification_Lab_RefNo.status}
                value={detailObj?.WPQ_Certification_Lab_RefNo}
                onChange={handleChangedetail}
                label="Laboratory Ref.Number"
                message={error.WPQ_Certification_Lab_RefNo.message}
                setError={setError}
                error={error}
                allowlistfunction={isAllowCustomSpecialCharacters}
                allowcharacters="&-"
                customMessage="Special Characters # % + $ ' < > = ; not allowed"
              ></SingleInput>
            </Col>
          </Row>
        </Row>
      </fieldset>
    );
  }
);

export default WPQ_Tubesheet_Page2;
